import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { findStep as findCourseStep } from 'modules/course/utils/course-steps'
import { CourseMenuLectureInterface, CourseQuizInterface } from '../../types/course-menu-interface'
import { CourseStepEnum } from '../../types/course-step-type'
import { getCoursePageQuery } from '../../utils/get-course-page-url'
import { parseQueryId } from '../../utils/parse-query-id'
import { useGetCoursePageUrl } from '../use-course-path-url'
import useCourseSteps from '../use-course-steps'
import { StepType } from './types'
import { getContentType } from './utils'

export default function useStepRouter() {
  const [currentStep, setCurrentStep] = useState<StepType>()
  const [isEnd, setIsEnd] = useState<boolean>(false)
  const [isStart, setIsStart] = useState<boolean>(false)
  const router = useRouter()
  const coursePath = useGetCoursePageUrl()
  const { courseSteps } = useCourseSteps()

  const { lectureId: rawLectureId, quizId: rawQuizId, preview } = router.query
  const [lectureId, quizId] = [rawLectureId, rawQuizId].map(parseQueryId)

  const isCurrentStep =
    (stepType: CourseStepEnum) =>
    (step: CourseMenuLectureInterface | CourseQuizInterface | undefined | null) => {
      const isMatchingType = stepType === currentStep?.type

      if (!isMatchingType) return false

      return !!(step && step.id === currentStep?.id)
    }

  const getStepUrl = (stepType: CourseStepEnum) => (id: number) =>
    `/school/course/${coursePath}/${stepType}/${id}`

  const isPreview = preview === 'true'

  const findStep = (): StepType | undefined => findCourseStep(courseSteps)

  const redirectToStep = (
    step: StepType | null | undefined,
    { shallow }: { shallow: boolean } = { shallow: true },
  ) => {
    if (!step) return

    return (
      coursePath &&
      router.push(
        getCoursePageQuery({
          coursePath,
          type: step.type,
          id: step.id,
        }),
        undefined,
        { shallow },
      )
    )
  }

  const getNextStep = () => {
    if (isEnd) return

    const currentStepIndex = courseSteps.findIndex(step => step.id === currentStep?.id)

    const nextStep = courseSteps.slice(currentStepIndex + 1).find(step => {
      if (step.type === 'quiz') return true

      if (step.type === 'lecture' && step.available) return true

      return false
    })

    return nextStep
  }

  const getPreviousStep = () => {
    if (isStart) return

    const currentStepIndex = courseSteps.findIndex(
      step => step.type === currentStep?.type && step.id === currentStep.id,
    )
    const previousStep = courseSteps.find(
      ({ id }) =>
        id ===
        courseSteps
          .slice(0, currentStepIndex)
          .reverse()
          .find(step => step.available)?.id,
    )
    return previousStep
  }

  const onNextStep = () => {
    const step = getNextStep()

    if (!step) return

    return redirectToStep(step, { shallow: false })
  }

  const onPreviousStep = () => {
    const step = getPreviousStep()

    if (!step) return

    return redirectToStep(step, { shallow: false })
  }

  const findStepById = (id?: number) =>
    id ? courseSteps?.find(step => step?.id === id) : undefined

  useEffect(() => {
    if (router.asPath) {
      const stepType = getContentType(router.asPath) as CourseStepEnum
      const id = stepType === CourseStepEnum.lecture ? lectureId : quizId
      const step = courseSteps.find(s => s.type === stepType && s.id === id)

      if (step) {
        setCurrentStep(step)
      }
    }
  }, [router.asPath, courseSteps])

  useEffect(() => {
    if (currentStep) {
      const currentStepIndex = courseSteps.findIndex(
        step => step.type === currentStep.type && step.id === currentStep.id,
      )

      const hasPrevious = courseSteps.some((step, i) => i < currentStepIndex && step.available)
      const hasNext = courseSteps.some((step, i) => i > currentStepIndex && step.available)

      setIsStart(!hasPrevious)
      setIsEnd(!hasNext)
    }
  }, [courseSteps, currentStep])

  return {
    isCurrentLecture: isCurrentStep(CourseStepEnum.lecture),
    isCurrentQuiz: isCurrentStep(CourseStepEnum.quiz),
    getLectureUrl: getStepUrl(CourseStepEnum.lecture),
    getQuizUrl: getStepUrl(CourseStepEnum.quiz),
    step: currentStep,
    findStepById,
    isPreview,
    findStep,
    redirectToStep,
    onNextStep,
    onPreviousStep,
    isEnd,
    isStart,
  }
}
